const { debounce, getElement } = window.BAO.Utils

class OmetriaAutocomplete {
  constructor () {
    this.enabled =
      document.body.classList.contains('omt-Autocomplete') &&
      typeof window.graphenehc !== 'undefined'

    if (!this.enabled) return

    this.input = getElement('.hd-Banner_SearchInput')
    this.container = getElement('.kuQuickSearchResultsContainer')
    this.closeButton = getElement('.hd-Banner_SearchClose')
    this.searchButton =
      getElement('.hd-Banner_Icon-search') ||
      getElement('[data-search-form-el="trigger"]')
    this.targetSelector = '#ometria-quick-search'

    if (!this.input || !this.container) return

    this.currentTerm = ''
    this.hasLoadedInitial = false
    this.target = null // Cache target element
    this.locationCache = null // Cache location checks
    this.searchPath = '/pages/search-results' // Cache search path
    this.productUrlRegex = /\/products\/([^/]+)/ // Cache regex
    this.escapeHtmlRegex = /[<>&"]/g // Cache regex for HTML escaping
    this.escapeHtmlMap = {
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      '&': '&amp;',
    } // Cache escape map
    this.previousActiveElement = null // Store element that had focus before opening

    this.handleInput = debounce(this.handleInput.bind(this), 150)
    this.handleFocus = this.handleFocus.bind(this)
    this.handleLinkClick = this.handleLinkClick.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)

    this.bindEvents()
    this.updateLocationCache()
  }

  // Cache location checks to avoid repeated property access
  updateLocationCache () {
    const { pathname, search } = window.location
    this.locationCache = {
      pathname,
      search,
      isOnSearchPage:
        pathname.includes('/pages/search') ||
        pathname.includes('/search') ||
        search.includes('q='),
    }
  }

  // Get cached target element or query it
  getTarget () {
    if (!this.target || !document.body.contains(this.target)) {
      this.target = document.querySelector(this.targetSelector)
    }
    return this.target
  }

  bindEvents () {
    this.input.addEventListener('focus', this.handleFocus)
    this.input.addEventListener('input', this.handleInput)
    this.container.addEventListener('click', this.handleLinkClick)
    // Add keyboard listener for focus trapping
    document.addEventListener('keydown', this.handleKeyDown)
    // Handle close button click
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => {
        this.hideContainer()
      })
    }
  }

  // Cleanup method for potential future use
  destroy () {
    if (this.input) {
      this.input.removeEventListener('focus', this.handleFocus)
      this.input.removeEventListener('input', this.handleInput)
    }
    if (this.container) {
      this.container.removeEventListener('click', this.handleLinkClick)
    }
    document.removeEventListener('keydown', this.handleKeyDown)
    this.target = null
  }

  handleLinkClick (event) {
    const link = event.target.closest('a')
    if (!link) return

    const href = link.getAttribute('href')
    if (!href) return

    event.stopPropagation()

    // Use cached location check
    this.updateLocationCache()
    const { isOnSearchPage } = this.locationCache

    if (
      isOnSearchPage &&
      (href.includes('?q=') ||
        href.includes('/pages/search') ||
        href.includes('/search'))
    ) {
      event.preventDefault()
      this.hideContainer()
      window.location.href = href
      return
    }

    this.hideContainer()
  }

  handleFocus () {
    this.showContainer()
    if (!this.hasLoadedInitial) {
      this.loadSuggestions('')
    }
  }

  handleInput (event) {
    const term = event.target.value.trim()

    if (term === this.currentTerm) {
      return
    }

    this.currentTerm = term

    if (term.length === 0) {
      this.loadSuggestions('')
      return
    }

    this.showContainer()
    this.generateQuickSuggestions(term)
    this.loadSuggestions(term)
  }

  showContainer () {
    // Store the element that had focus before opening
    this.previousActiveElement = document.activeElement

    this.container.removeAttribute('aria-hidden')
    this.container.setAttribute('aria-hidden', 'false')
    this.container.style.cssText = ''
    this.container.style.display = ''
    document.body.classList.add('util-Search-open')
  }

  hideContainer () {
    this.container.setAttribute('aria-hidden', 'true')
    this.container.style.cssText = 'display: none !important'
    document.body.classList.remove('util-Search-open')

    // Return focus to the element that had focus before opening (or the input)
    if (
      this.previousActiveElement &&
      document.body.contains(this.previousActiveElement)
    ) {
      this.previousActiveElement.focus()
    } else if (this.input) {
      this.input.focus()
    }
    this.previousActiveElement = null
  }

  // Get all focusable elements within the autocomplete (including input, search button, and close button)
  getFocusableElements () {
    const focusableElements = []

    // Start with the search input
    if (this.input) {
      focusableElements.push(this.input)
    }

    // Add search submit button after input
    if (this.searchButton) {
      const searchButtonStyle = window.getComputedStyle(this.searchButton)
      if (
        searchButtonStyle.display !== 'none' &&
        searchButtonStyle.visibility !== 'hidden' &&
        searchButtonStyle.opacity !== '0'
      ) {
        focusableElements.push(this.searchButton)
      }
    }

    // Add close button after search button
    if (this.closeButton) {
      const closeStyle = window.getComputedStyle(this.closeButton)
      if (
        closeStyle.display !== 'none' &&
        closeStyle.visibility !== 'hidden' &&
        closeStyle.opacity !== '0'
      ) {
        focusableElements.push(this.closeButton)
      }
    }

    // Add focusable elements from the container (not the search form buttons)
    const target = this.getTarget()
    if (target && this.container) {
      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
        '[role="button"]:not([tabindex="-1"])',
      ].join(', ')

      // Search both target and container to catch all autocomplete elements
      const allElements = [
        ...target.querySelectorAll(focusableSelectors),
        ...this.container.querySelectorAll(focusableSelectors),
      ]

      const containerElements = Array.from(allElements).filter(el => {
        // Don't include input, search button, or close button (already added separately)
        if (
          el === this.input ||
          el === this.searchButton ||
          el === this.closeButton
        ) {
          return false
        }

        // Filter out elements that are not visible
        const style = window.getComputedStyle(el)
        return (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.opacity !== '0' &&
          !el.hasAttribute('aria-hidden') &&
          el.offsetParent !== null // Additional check for visibility
        )
      })

      // Remove duplicates
      const uniqueElements = Array.from(new Set(containerElements))
      focusableElements.push(...uniqueElements)
    }

    return focusableElements
  }

  handleKeyDown (event) {
    // Only handle if autocomplete is open
    if (this.container.getAttribute('aria-hidden') === 'true') {
      return
    }

    // Handle Escape key to close
    if (event.key === 'Escape') {
      event.preventDefault()
      this.hideContainer()
      return
    }

    // Handle Tab key for focus trapping
    if (event.key === 'Tab') {
      const focusableElements = this.getFocusableElements()

      if (focusableElements.length === 0) {
        // No focusable elements, allow default behavior
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      const currentElement = document.activeElement

      // Check if focus is on the search submit button
      const isSearchSubmitButton =
        currentElement === this.searchButton ||
        currentElement.classList?.contains('hd-Banner_Icon-search') ||
        (currentElement.closest('.hd-Banner_SearchForm') &&
          currentElement.type === 'submit' &&
          currentElement !== this.closeButton)

      // If focus is on search submit button and it's not in our array, redirect
      if (isSearchSubmitButton && !focusableElements.includes(currentElement)) {
        event.preventDefault()
        if (event.shiftKey) {
          // Shift+Tab from submit button: go to input
          firstElement.focus()
        } else {
          // Tab from submit button: go to close button (index 2: input=0, search=1, close=2)
          const closeButtonIndex = focusableElements.indexOf(this.closeButton)
          if (closeButtonIndex !== -1) {
            focusableElements[closeButtonIndex].focus()
          } else if (lastElement) {
            lastElement.focus()
          } else {
            firstElement.focus()
          }
        }
        return
      }

      // Check if current element is part of the autocomplete focus cycle
      const isInFocusCycle =
        focusableElements.includes(currentElement) ||
        (this.container.contains(currentElement) && !isSearchSubmitButton)

      if (!isInFocusCycle) {
        // Focus is outside, trap it to first element (input)
        event.preventDefault()
        firstElement.focus()
        return
      }

      const currentIndex = focusableElements.indexOf(currentElement)
      const searchButtonIndex = this.searchButton
        ? focusableElements.indexOf(this.searchButton)
        : -1
      const closeButtonIndex = focusableElements.indexOf(this.closeButton)
      const containerElements = focusableElements.filter(
        el =>
          el !== firstElement &&
          el !== this.searchButton &&
          el !== this.closeButton,
      )
      const firstContainerElement =
        containerElements.length > 0 ? containerElements[0] : null
      const lastContainerElement =
        containerElements.length > 0
          ? containerElements[containerElements.length - 1]
          : null

      // Shift+Tab: going backwards
      if (event.shiftKey) {
        if (currentIndex === 0 || currentElement === firstElement) {
          // On first element (input), loop to last container element
          if (lastContainerElement) {
            event.preventDefault()
            lastContainerElement.focus()
          }
        } else if (
          currentElement === this.searchButton ||
          currentIndex === searchButtonIndex
        ) {
          // On search button, go to input
          event.preventDefault()
          firstElement.focus()
        } else if (
          currentElement === this.closeButton ||
          currentIndex === closeButtonIndex
        ) {
          // On close button, go to search button
          if (searchButtonIndex !== -1) {
            event.preventDefault()
            focusableElements[searchButtonIndex].focus()
          } else {
            event.preventDefault()
            firstElement.focus()
          }
        } else if (currentElement === firstContainerElement) {
          // On first container element, go to close button
          if (closeButtonIndex !== -1) {
            event.preventDefault()
            focusableElements[closeButtonIndex].focus()
          }
        }
      } else {
        // Tab: going forwards
        if (currentElement === firstElement) {
          // On input, go to search button (index 1)
          if (searchButtonIndex !== -1) {
            event.preventDefault()
            focusableElements[searchButtonIndex].focus()
          } else if (closeButtonIndex !== -1) {
            event.preventDefault()
            focusableElements[closeButtonIndex].focus()
          }
        } else if (
          currentElement === this.searchButton ||
          currentIndex === searchButtonIndex
        ) {
          // On search button, go to close button (index 2)
          if (closeButtonIndex !== -1) {
            event.preventDefault()
            focusableElements[closeButtonIndex].focus()
          } else if (firstContainerElement) {
            event.preventDefault()
            firstContainerElement.focus()
          } else {
            event.preventDefault()
            firstElement.focus()
          }
        } else if (
          currentElement === this.closeButton ||
          currentIndex === closeButtonIndex
        ) {
          // On close button, go to first container element (or loop to input if no container elements)
          if (firstContainerElement) {
            event.preventDefault()
            firstContainerElement.focus()
          } else {
            event.preventDefault()
            firstElement.focus()
          }
        } else if (
          currentElement === lastContainerElement ||
          (lastContainerElement &&
            currentIndex === focusableElements.indexOf(lastContainerElement))
        ) {
          // On last container element, loop to first (input)
          event.preventDefault()
          firstElement.focus()
        }
      }
    }
  }

  loadSuggestions (term) {
    if (!window.graphenehc?.load) {
      console.warn('[Ometria Autocomplete] graphenehc.load not available')
      return
    }

    const hasSearchTerm = term && term.length > 0
    const templateId = hasSearchTerm ? 'autocomplete' : 'autocomplete_home'

    const target = this.getTarget()
    if (target) {
      const quickSuggestions = target.querySelector(
        '.klevuAutoSuggestionsWrap.klevuAutosuggestions',
      )
      if (!(quickSuggestions && hasSearchTerm)) {
        target.innerHTML = ''
      }
    }

    // Build URL with search query
    const url = hasSearchTerm
      ? `/search?q=${encodeURIComponent(term)}`
      : this.locationCache.pathname + this.locationCache.search

    const args = {}
    if (hasSearchTerm) {
      args.q = term
      args.query = `/search?q=${encodeURIComponent(term)}`
    }

    // Cache include arrays to avoid recreation
    const includeWithTerm = [
      'products',
      'categories',
      'pages',
      'popular_searches',
      'autocomplete_no_results',
    ]
    const includeWithoutTerm = [
      'products',
      'categories',
      'pages',
      'popular_searches',
      'autocomplete_home_recs',
    ]
    const include = hasSearchTerm ? includeWithTerm : includeWithoutTerm

    const pageContext = {
      currency_id: window.ometriaCurrency || window.Shopify?.currency?.active,
    }
    if (window.ometriaLanguageId) { pageContext.language_id = window.ometriaLanguageId }
    if (hasSearchTerm) {
      pageContext.search_text = term
    }

    window.graphenehc.load({
      template: { id: templateId, target: this.targetSelector },
      include,
      url,
      args,
      page_context: pageContext,
      success: response => {
        this.hasLoadedInitial = true
        this.wrapContent()
        this.removeEmptyLinks()
        this.showContainer()
        setTimeout(() => {
          this.generateSearchSuggestions(term)
          // Initialize product card carousels after content loads
          this.initializeProductCarousels()
        }, 50)
      },
      error: error => {
        console.error('[Ometria Autocomplete] Load error:', error)
      },
    })
  }

  wrapContent () {
    const target = this.getTarget()
    if (!target) return

    if (target.querySelector('.klv-QuickSearch')) return

    if (!this.container.classList.contains('kuSearchBoxWithTerm')) {
      this.container.classList.add('kuSearchBoxWithTerm')
    }
  }

  // Helper: Convert handle to readable text (cached logic)
  handleToReadable (handle) {
    return handle.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  // Helper: Escape HTML entities efficiently
  escapeHtml (text) {
    return text.replace(this.escapeHtmlRegex, m => this.escapeHtmlMap[m])
  }

  // Helper: Extract product handle from URL
  extractProductHandle (url) {
    const match = url.match(this.productUrlRegex)
    return match ? match[1] : null
  }

  generateQuickSuggestions (term) {
    const target = this.getTarget()
    if (!target) return

    const termLower = term.toLowerCase()
    const termWords = termLower.split(/\s+/) // Cache split
    const termWordCount = termWords.length
    const suggestions = new Set()

    // 1. Check previous searches that match
    const previousLinks = target.querySelectorAll(
      '.klv-QuickSearch_Text a[href*="?q="]',
    )
    for (const link of previousLinks) {
      const text = (link.textContent || '').trim()
      if (text && text.toLowerCase().startsWith(termLower)) {
        suggestions.add(text)
      }
    }

    // 2. Extract suggestions from product URLs
    const productCards = target.querySelectorAll('.klv-QuickSearch_Product')
    if (productCards.length > 0) {
      for (const card of productCards) {
        const asyncEl = card.querySelector('section-async')
        if (!asyncEl) continue

        const url = asyncEl.getAttribute('url') || ''
        const handle = this.extractProductHandle(url)
        if (!handle) continue

        const readable = this.handleToReadable(handle)
        const readableLower = readable.toLowerCase()

        if (!readableLower.includes(termLower)) continue

        const words = readable.split(/\s+/)
        const maxIndex = words.length - termWordCount

        for (let i = 0; i <= maxIndex; i++) {
          const slice = words
            .slice(i, i + termWordCount)
            .join(' ')
            .toLowerCase()
          if (slice === termLower) {
            suggestions.add(term)
            if (i + termWordCount < words.length) {
              const suggestion = words.slice(i, i + termWordCount + 1).join(' ')
              if (suggestion.toLowerCase() !== termLower) {
                suggestions.add(suggestion)
              }
            }
            if (i > 0) {
              const suggestion = words.slice(i - 1, i + termWordCount).join(' ')
              if (suggestion.toLowerCase() !== termLower) {
                suggestions.add(suggestion)
              }
            }
          }
        }
      }
    }

    if (suggestions.size === 0) return

    const suggestionsArray = Array.from(suggestions)
      .filter(s => s.toLowerCase() !== termLower)
      .slice(0, 6)

    this.updateSuggestionsUI(target, suggestionsArray, term, false)
  }

  generateSearchSuggestions (term) {
    if (!term || term.trim().length === 0) return

    const target = this.getTarget()
    if (!target) return

    const existingSuggestionsSection = target.querySelector(
      '.klevuAutoSuggestionsWrap.klevuAutosuggestions',
    )
    const existingSuggestions = existingSuggestionsSection?.querySelectorAll(
      '.klv-QuickSearch_AutoSuggestion',
    )
    if (existingSuggestions && existingSuggestions.length > 1) {
      return
    }

    const suggestions = new Set()
    const termLower = term.toLowerCase()
    const termWords = termLower.split(/\s+/) // Cache split
    const termWordCount = termWords.length

    const productCards = target.querySelectorAll('.klv-QuickSearch_Product')
    for (const card of productCards) {
      const asyncEl = card.querySelector('section-async')
      if (!asyncEl) continue

      const url = asyncEl.getAttribute('url') || ''
      const handle = this.extractProductHandle(url)
      if (!handle) continue

      const readable = this.handleToReadable(handle)
      const readableLower = readable.toLowerCase()

      const hasMatch =
        readableLower.includes(termLower) ||
        readableLower.split(/\s+/).some(word => word.startsWith(termLower))

      if (!hasMatch) continue

      const words = readable.split(/\s+/)
      const maxIndex = words.length - termWordCount

      for (let i = 0; i <= maxIndex; i++) {
        const slice = words
          .slice(i, i + termWordCount)
          .join(' ')
          .toLowerCase()
        const isMatch =
          slice === termLower ||
          slice.startsWith(termLower) ||
          words
            .slice(i, i + termWordCount)
            .some(w => w.toLowerCase().startsWith(termLower))

        if (isMatch) {
          let completeTerm = term
          const matchingWord = words
            .slice(i, i + termWordCount)
            .find(w => w.toLowerCase().startsWith(termLower))
          if (matchingWord) {
            completeTerm = matchingWord
          } else if (i + termWordCount <= words.length) {
            const fullSlice = words.slice(i, i + termWordCount).join(' ')
            if (fullSlice.toLowerCase().startsWith(termLower)) {
              completeTerm = fullSlice
            }
          }

          if (completeTerm.toLowerCase() !== termLower) {
            suggestions.add(completeTerm)
          }

          if (i + termWordCount < words.length) {
            suggestions.add(`${completeTerm} ${words[i + termWordCount]}`)
          }

          if (i > 0) {
            suggestions.add(`${words[i - 1]} ${completeTerm}`)
          }

          if (i > 0 && i + termWordCount < words.length) {
            suggestions.add(
              `${words[i - 1]} ${completeTerm} ${words[i + termWordCount]}`,
            )
          }

          if (i + termWordCount + 1 < words.length) {
            const suggestion4 = words
              .slice(i, i + termWordCount + 2)
              .join(' ')
              .replace(new RegExp(`^${term}`, 'i'), completeTerm)
            suggestions.add(suggestion4)
          }
        }
      }
    }

    const suggestionsArray = Array.from(suggestions)
      .filter(s => s.toLowerCase() !== termLower)
      .slice(0, 6)

    if (suggestionsArray.length === 0) return

    this.updateSuggestionsUI(
      target,
      suggestionsArray,
      term,
      true,
      existingSuggestionsSection,
    )
  }

  // Consolidated method to update suggestions UI (reduces code duplication)
  updateSuggestionsUI (
    target,
    suggestionsArray,
    term,
    highlightTerm = false,
    existingSection = null,
  ) {
    let suggestionsBlock =
      existingSection ||
      target.querySelector('.klevuAutoSuggestionsWrap.klevuAutosuggestions')

    if (!suggestionsBlock) {
      const linksContainer = target.querySelector('.klv-QuickSearch_Links')
      if (!linksContainer) return

      const suggestionsBlockContainer = linksContainer.querySelector(
        '.klevuSuggestionsBlock',
      )
      if (!suggestionsBlockContainer) return

      suggestionsBlock = document.createElement('div')
      suggestionsBlock.className =
        'klv-QuickSearch_Block klevuAutoSuggestionsWrap klevuAutosuggestions'
      suggestionsBlock.innerHTML = `
        <div class="klevuSuggestionHeading">
          <h3 class="klv-QuickSearch_Title fz-18_111">Search Suggestions</h3>
        </div>
        <ul class="klv-QuickSearch_Text rte-RichText klv-RichText fz-14_150-light"></ul>
      `
      suggestionsBlockContainer.insertBefore(
        suggestionsBlock,
        suggestionsBlockContainer.firstChild,
      )
    }

    let suggestionsList = suggestionsBlock.querySelector(
      '.klv-QuickSearch_Text',
    )
    if (!suggestionsList) {
      suggestionsList = document.createElement('ul')
      suggestionsList.className =
        'klv-QuickSearch_Text rte-RichText klv-RichText fz-14_150-light'
      suggestionsBlock.appendChild(suggestionsList)
    }

    // For quick suggestions, check if update is needed
    if (!highlightTerm) {
      const currentSuggestions = Array.from(
        suggestionsList.querySelectorAll('li a b'),
      ).map(el => el.textContent.trim())

      if (
        suggestionsArray.length > 0 &&
        JSON.stringify(currentSuggestions) === JSON.stringify(suggestionsArray)
      ) {
        return // No update needed
      }
    }

    // Batch DOM manipulation using DocumentFragment
    const fragment = document.createDocumentFragment()
    const termRegex = highlightTerm
      ? new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
      : null

    for (const suggestion of suggestionsArray) {
      const li = document.createElement('li')
      li.className = 'klv-QuickSearch_AutoSuggestion'
      li.setAttribute('tabindex', '-1')
      const encodedSuggestion = encodeURIComponent(suggestion)

      let content
      if (highlightTerm && termRegex) {
        const escaped = this.escapeHtml(suggestion)
        content = escaped.replace(termRegex, '<b>$1</b>')
      } else {
        content = `<b>${this.escapeHtml(suggestion)}</b>`
      }

      li.innerHTML = `
        <a target="_self" href="${
          this.searchPath
        }?q=${encodedSuggestion}" data-content="${suggestion.replace(
        /"/g,
        '&quot;',
      )}">
          ${content}
        </a>
      `
      fragment.appendChild(li)
    }

    suggestionsList.innerHTML = ''
    suggestionsList.appendChild(fragment)
  }

  removeEmptyLinks () {
    const target = this.getTarget()
    if (!target) return

    const popularSearchLinks = target.querySelectorAll(
      '.klv-QuickSearch_Text li > a',
    )

    // Batch removals to reduce reflows
    const itemsToRemove = []
    for (const link of popularSearchLinks) {
      const textContent = (link.textContent || '').trim()
      const href = link.getAttribute('href') || ''
      const isEmptyHref =
        href === '' ||
        href === '/pages/search-results?q=' ||
        href.includes('?q=&')

      if (textContent === '' || isEmptyHref) {
        const listItem = link.closest('li')
        if (listItem) {
          itemsToRemove.push(listItem)
        }
      }
    }

    // Remove all at once
    for (const item of itemsToRemove) {
      item.remove()
    }
  }

  /**
   * Initialize product card carousels after async content loads
   * This ensures the image slider functionality works in autocomplete product cards
   */
  initializeProductCarousels () {
    const target = this.getTarget()
    if (!target) return

    // Watch for section-async elements to finish loading
    const asyncElements = target.querySelectorAll(
      'section-async[section-id="product-card"]',
    )

    if (asyncElements.length === 0) return

    // Use MutationObserver to watch for when async elements finish loading
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'aria-busy'
        ) {
          const element = mutation.target
          // When aria-busy changes from "true" to "false", the content has loaded
          if (element.getAttribute('aria-busy') === 'false') {
            // Find product-card-carousel elements within this async element
            const carousels = element.querySelectorAll('product-card-carousel')
            for (const carousel of carousels) {
              // Check if carousel actually has multiple images
              const slides = carousel.querySelectorAll(
                '[data-product-card-carousel-el="slide"]',
              )
              const navigation = carousel.querySelector(
                '[data-product-card-carousel-el="navigation"]',
              )

              // Hide navigation if there's only one image (safety check)
              if (slides.length <= 1 && navigation) {
                navigation.style.display = 'none'
                continue // Skip initialization for single-image carousels
              }

              // Show navigation if it was hidden and we have multiple images
              if (slides.length > 1 && navigation) {
                navigation.style.display = ''
              }

              // Prevent carousel navigation clicks from closing the autocomplete
              const navButtons = carousel.querySelectorAll(
                '[data-product-card-carousel-el="navigation.prev"], [data-product-card-carousel-el="navigation.next"]',
              )
              for (const button of navButtons) {
                button.addEventListener('click', e => {
                  e.stopPropagation()
                })
              }

              // Ensure the carousel is properly initialized
              // The custom element should auto-initialize, but we can trigger setupListeners
              // if it hasn't been called yet
              if (
                carousel.setupListeners &&
                typeof carousel.setupListeners === 'function'
              ) {
                // Re-setup listeners in case they weren't attached
                carousel.setupListeners()
              }

              // Ensure images are loaded before calculating scroll positions
              const images = carousel.querySelectorAll('img')
              if (images.length > 0) {
                // Wait for images to load, then ensure carousel is ready
                Promise.all(
                  Array.from(images).map(
                    img =>
                      new Promise(resolve => {
                        if (img.complete) {
                          resolve()
                        } else {
                          img.addEventListener('load', resolve, { once: true })
                          img.addEventListener('error', resolve, { once: true })
                        }
                      }),
                  ),
                ).then(() => {
                  // Images loaded - carousel should now work properly
                  // Carousel scroll calculations will be correct once images are loaded
                })
              }
            }
          }
        }

        // Also watch for when innerHTML changes (content is inserted)
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if any added nodes are product-card-carousel elements
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const carousels = node.querySelectorAll
                ? node.querySelectorAll('product-card-carousel')
                : []
              if (node.tagName === 'PRODUCT-CARD-CAROUSEL') {
                carousels.push(node)
              }

              for (const carousel of carousels) {
                // Check if carousel actually has multiple images
                const slides = carousel.querySelectorAll(
                  '[data-product-card-carousel-el="slide"]',
                )
                const navigation = carousel.querySelector(
                  '[data-product-card-carousel-el="navigation"]',
                )

                // Hide navigation if there's only one image
                if (slides.length <= 1 && navigation) {
                  navigation.style.display = 'none'
                  continue
                }

                // Show navigation if we have multiple images
                if (slides.length > 1 && navigation) {
                  navigation.style.display = ''
                }

                // Prevent carousel navigation clicks from closing the autocomplete
                const navButtons = carousel.querySelectorAll(
                  '[data-product-card-carousel-el="navigation.prev"], [data-product-card-carousel-el="navigation.next"]',
                )
                for (const button of navButtons) {
                  button.addEventListener('click', e => {
                    e.stopPropagation()
                  })
                }

                if (
                  carousel.setupListeners &&
                  typeof carousel.setupListeners === 'function'
                ) {
                  carousel.setupListeners()
                }
              }
            }
          }
        }
      }
    })

    // Observe all async elements for changes
    for (const asyncEl of asyncElements) {
      observer.observe(asyncEl, {
        attributes: true,
        attributeFilter: ['aria-busy'],
        childList: true,
        subtree: true,
      })
    }

    // Also check for already-loaded carousels
    setTimeout(() => {
      const existingCarousels = target.querySelectorAll('product-card-carousel')
      for (const carousel of existingCarousels) {
        // Check if carousel actually has multiple images
        const slides = carousel.querySelectorAll(
          '[data-product-card-carousel-el="slide"]',
        )
        const navigation = carousel.querySelector(
          '[data-product-card-carousel-el="navigation"]',
        )

        // Hide navigation if there's only one image
        if (slides.length <= 1 && navigation) {
          navigation.style.display = 'none'
          continue
        }

        // Show navigation if we have multiple images
        if (slides.length > 1 && navigation) {
          navigation.style.display = ''
        }

        // Prevent carousel navigation clicks from closing the autocomplete
        const navButtons = carousel.querySelectorAll(
          '[data-product-card-carousel-el="navigation.prev"], [data-product-card-carousel-el="navigation.next"]',
        )
        for (const button of navButtons) {
          button.addEventListener('click', e => {
            e.stopPropagation()
          })
        }

        if (
          carousel.setupListeners &&
          typeof carousel.setupListeners === 'function'
        ) {
          carousel.setupListeners()
        }
      }
    }, 100)
  }
}

// Initialise once DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // eslint-disable-next-line no-new
  new OmetriaAutocomplete()
})
